/**
 * The annotation engine's own IndexedDB database. Listed in offline/wipe.ts LOCAL_DB_NAMES, so
 * logout and user switches delete it (the engine drops its in-memory state on 'versionchange').
 */
import Dexie, { type Table, type Transaction } from 'dexie';
import type { AnnotationObject } from '../../../shared/annotations';
import type { LayerRecord } from './model';
import { Outbox, sameOpVersion, type OpRecord } from './queue';

export const ANNOTATION_DB_NAME = 'kgr-annotations';

export interface CursorRow {
  layerId: string;
  seq: number;
  /** ANNOTATION_DATA_VERSION of the client that wrote it (absent: written before version 2). */
  v?: number;
}

export interface PrefRow {
  layerId: string;
  visible: boolean;
}

export interface MetaRow {
  k: string;
  v: unknown;
}

export const META = {
  /** Owner of the data in this database (a different signed-in user clears it). */
  userId: 'userId',
  deviceId: 'deviceId',
  lastHlc: 'lastHlc',
  /** ANNOTATION_DATA_VERSION the stored pull cursors were made with (no row = before version 2). */
  dataVersion: 'dataVersion',
  active: (sheetFileId: string) => `active:${sheetFileId}`,
  /**
   * "Fela lög af eldri útgáfu" is on for this sheet: { shown: layer ids shown anyway }. No row = off.
   * A per-sheet filter, applied only while a layer is drawn on an older revision (see SheetStore).
   */
  hideStale: (sheetFileId: string) => `hideStale:${sheetFileId}`,
} as const;

/**
 * Annotation data this client can read. Bump it when the model (shared/annotations.ts) accepts data an
 * older client drops as invalid: such a client still advanced its pull cursors past those rows, so
 * after the update every layer is fetched in full once (applyDataVersion).
 *  2 – connectors with a pin `size`.
 */
export const ANNOTATION_DATA_VERSION = 2;

/** The tables applyDataVersion touches (an AnnotationDb, or a fake in tests). */
export interface DataVersionTables {
  meta: { get(k: string): PromiseLike<MetaRow | undefined>; put(row: MetaRow): PromiseLike<unknown> };
  cursors: { clear(): PromiseLike<unknown> };
}

/**
 * Once per device, when the stored data version is older (or missing): forgets every layer's pull
 * cursor, so the next pull of each sheet fetches its layers in full — rows an older client dropped
 * come back. Local objects and the outbox stay (pending edits stay authoritative in the merge).
 * Returns whether the cursors were cleared. Call inside one transaction over meta + cursors, before
 * any sheet store reads cursors (Generation.init). A tab still on an older bundle may write cursors
 * again afterwards: those rows carry no (or an older) `v` and restoreCursors ignores them.
 */
export async function applyDataVersion(db: DataVersionTables): Promise<boolean> {
  const row = await db.meta.get(META.dataVersion);
  const stored = typeof row?.v === 'number' ? row.v : 0;
  if (stored >= ANNOTATION_DATA_VERSION) return false;
  await db.cursors.clear();
  await db.meta.put({ k: META.dataVersion, v: ANNOTATION_DATA_VERSION });
  return true;
}

/** A layer's pull cursor as this client stores it: tagged with the data version it was pulled with. */
export function cursorRow(layerId: string, seq: number): CursorRow {
  return { layerId, seq, v: ANNOTATION_DATA_VERSION };
}

/**
 * Loads stored cursor rows into `into` (layers already there keep theirs). A row from an older data
 * version — e.g. written by a tab still on the old bundle after applyDataVersion ran, which dropped
 * rows it could not read — is ignored: that layer is pulled in full.
 */
export function restoreCursors(into: Map<string, number>, rows: readonly (CursorRow | undefined)[]): void {
  for (const c of rows) {
    if (c && (c.v ?? 0) >= ANNOTATION_DATA_VERSION && !into.has(c.layerId)) into.set(c.layerId, c.seq);
  }
}

/** Version 1 kept the outbox in `ops`, keyed by seq: move it to `outbox`, keyed by the collapse key. */
async function migrateOps(tx: Transaction): Promise<void> {
  const old = (await tx.table('ops').toArray()) as OpRecord[];
  if (old.length) await tx.table('outbox').bulkPut([...Outbox.restore(old).outbox.ordered()]);
}

export class AnnotationDb extends Dexie {
  layers!: Table<LayerRecord, string>;
  /** Live objects only (deleted ones leave; a pending delete lives in the outbox). */
  objects!: Table<AnnotationObject, string>;
  cursors!: Table<CursorRow, string>;
  /** The device's outbox, keyed by the op's collapse key (shared by every open tab). */
  outbox!: Table<OpRecord, string>;
  prefs!: Table<PrefRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super(ANNOTATION_DB_NAME);
    this.version(1).stores({
      layers: 'id, sheetFileId',
      objects: 'id, layerId',
      cursors: 'layerId',
      ops: 'seq, key, userId, layerId, sheetFileId',
      prefs: 'layerId',
      meta: 'k',
    });
    this.version(2).stores({ outbox: 'key, seq, userId, layerId, sheetFileId' }).upgrade(migrateOps);
    this.version(3).stores({ ops: null });
  }

  /**
   * Deletes the stored copies of these (sent or dropped) ops — only where storage still holds
   * that same version, so a newer version queued by another tab is kept. Call inside a
   * transaction that covers `outbox`.
   */
  async deleteOps(recs: readonly OpRecord[]): Promise<void> {
    if (!recs.length) return;
    const stored = await this.outbox.bulkGet(recs.map((r) => r.key));
    const keys: string[] = [];
    recs.forEach((r, i) => {
      const s = stored[i];
      if (s && sameOpVersion(s, r)) keys.push(r.key);
    });
    if (keys.length) await this.outbox.bulkDelete(keys);
  }
}
